import React, { memo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, CheckCircle, Loader2, Gift } from 'lucide-react';

interface NewsletterSignupProps {
  variant?: 'default' | 'compact' | 'popup';
  onSuccess?: () => void;
  className?: string;
}

// Web3Forms API Key - same as contact form (already configured)
const WEB3FORMS_KEY = import.meta.env.VITE_WEB3FORMS_KEY || '53b5f57e-0a58-4202-a849-b98138833662';

/**
 * Newsletter signup component with lead magnet offer.
 * Uses Web3Forms API (same as contact form - already configured).
 * Works on Vercel static hosting without any additional setup.
 */
const NewsletterSignup = memo(function NewsletterSignup({
  variant = 'default',
  onSuccess,
  className = '',
}: NewsletterSignupProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setStatus('error');
      setErrorMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');

    try {
      // Use Web3Forms to capture newsletter signup
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: '🎁 New 15% Discount Request - Website Project',
          from_name: 'Discount Signup',
          email: email,
          message: `New visitor requesting 15% discount code:\n\nEmail: ${email}\n\nDiscount Code: DWB15OFF\n\nSource: Exit Intent Popup / Newsletter Form\n\n---\nRemember to send them the discount code and follow up!`,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to subscribe');
      }
      
      setStatus('success');
      setEmail('');
      onSuccess?.();
      
      // Reset after 5 seconds
      setTimeout(() => setStatus('idle'), 5000);
    } catch (error) {
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
      // Reset error after 3 seconds
      setTimeout(() => {
        setStatus('idle');
        setErrorMessage('');
      }, 3000);
    }
  };

  if (variant === 'compact') {
    return (
      <form onSubmit={handleSubmit} className={`flex gap-2 ${className}`}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          className="flex-grow px-4 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          disabled={status === 'loading' || status === 'success'}
        />
        <motion.button
          type="submit"
          disabled={status === 'loading' || status === 'success'}
          className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {status === 'loading' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : status === 'success' ? (
            <CheckCircle className="w-4 h-4" />
          ) : (
            'Subscribe'
          )}
        </motion.button>
      </form>
    );
  }

  if (variant === 'popup') {
    return (
      <div className={`text-center ${className}`}>
        <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Gift className="w-8 h-8 text-green-500" />
        </div>
        <h3 className="text-2xl font-bold mb-2">Claim Your 15% Discount</h3>
        <p className="text-muted-foreground mb-6">
          Enter your email and we'll send you your exclusive discount code. Use it on any project — no strings attached!
        </p>
        
        <AnimatePresence mode="wait">
          {status === 'success' ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-3 py-4"
            >
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
              <p className="font-medium text-green-600 dark:text-green-400">
                🎉 Check your inbox! Your 15% discount code is on the way.
              </p>
            </motion.div>
          ) : (
            <motion.form
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full pl-12 pr-4 py-3 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={status === 'loading'}
                />
              </div>
              
              {status === 'error' && (
                <p className="text-red-500 text-sm">{errorMessage}</p>
              )}
              
              <motion.button
                type="submit"
                disabled={status === 'loading'}
                className="w-full py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {status === 'loading' ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Gift className="w-5 h-5" />
                    <span>Get Free Checklist</span>
                  </>
                )}
              </motion.button>
              
              <p className="text-xs text-muted-foreground">
                No spam, ever. Unsubscribe anytime.
              </p>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Default variant - inline form with lead magnet mention
  return (
    <div className={`bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-6 border border-primary/20 ${className}`}>
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center flex-shrink-0">
          <Gift className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h4 className="font-bold text-lg mb-1">Free Website Checklist</h4>
          <p className="text-muted-foreground text-sm">
            Get our comprehensive guide to building a successful website.
          </p>
        </div>
      </div>
      
      <AnimatePresence mode="wait">
        {status === 'success' ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-green-600 dark:text-green-400 font-medium"
          >
            <CheckCircle className="w-5 h-5" />
            <span>Success! Check your inbox.</span>
          </motion.div>
        ) : (
          <motion.form
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onSubmit={handleSubmit}
            className="flex gap-2"
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-grow px-4 py-3 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={status === 'loading'}
            />
            <motion.button
              type="submit"
              disabled={status === 'loading'}
              className="px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 whitespace-nowrap"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {status === 'loading' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Get It Free'
              )}
            </motion.button>
          </motion.form>
        )}
      </AnimatePresence>
      
      {status === 'error' && (
        <p className="text-red-500 text-sm mt-2">{errorMessage}</p>
      )}
    </div>
  );
});

export default NewsletterSignup;
